import { Alert, Box, Card, CardActions, CardContent, CardHeader, Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";

import { EditButton } from "~/components/Buttons";
import describeSavedRuleCondition from "~/components/grid/conditionalFormatting/describeSavedRuleCondition";
import SpacedGridContainer from "~/components/SpacedGridContainer";
import SpacedGridItem from "~/components/SpacedGridItem";

interface SavedRulesProps {
	editRule: (rule: GridFormattingRule) => void;
	rules: GridFormattingRule[];
	toggleActive: (id: string) => void;
}

const SAVED_RULES_KEY_PREFIX = "grid.conditionalFormatting.savedRules";

const SavedRules: React.FC<SavedRulesProps> = ({ editRule, rules, toggleActive }) => {
	const { t } = useTranslation("common");

	const displayRule = (rule: GridFormattingRule) =>
		rule.conditions.map((condition, i) => {
			const description = describeSavedRuleCondition(condition, rule.ruleType);
			return (
				<Box key={i}>
					{i > 0 ? t(`${SAVED_RULES_KEY_PREFIX}.lines.${rule.andor}`, { condition: description }) : description}
				</Box>
			);
		});

	if (!rules || rules.length === 0) {
		return (
			<SpacedGridContainer padding={1}>
				<SpacedGridItem maxCols={1}>
					<Alert severity="info" variant="standard">
						{t(`${SAVED_RULES_KEY_PREFIX}.empty`)}
					</Alert>
				</SpacedGridItem>
			</SpacedGridContainer>
		);
	}

	return (
		<SpacedGridContainer alignItems="stretch" padding={1}>
			{rules.map((rule, index) => (
				<SpacedGridItem
					component={Card}
					key={index}
					maxCols={3}
					sx={{
						display: "flex",
						flexDirection: "column"
					}}
				>
					<CardHeader
						slotProps={{ title: { display: "flex", fontSize: "1rem", justifyContent: "space-between" } }}
						sx={{
							backgroundImage:
								rule.styleType === "single"
									? `linear-gradient(${rule.style.backgroundColor}, ${rule.style?.backgroundColor})`
									: `linear-gradient(to right,${rule.style.backgroundColor}, ${rule.style2?.backgroundColor})`,
							color: rule.style.color
						}}
						title={
							<>
								<Box display={"inline"}>
									{t(`${SAVED_RULES_KEY_PREFIX}.headers.${rule.ruleType}.${rule.applyToRow ? "row" : "cell"}`)}
								</Box>
								<Box
									display={"inline"}
									sx={{ color: rule.styleType === "double" ? rule.style2.color : rule.style.color }}
								>
									{rule.defaultActive && t(`${SAVED_RULES_KEY_PREFIX}.default`)}
								</Box>
							</>
						}
					></CardHeader>
					<CardContent>{displayRule(rule)}</CardContent>
					<CardActions
						disableSpacing
						sx={{
							display: "flex",
							justifyContent: "space-between",
							mt: "auto"
						}}
					>
						{" "}
						<FormControlLabel
							control={<Checkbox checked={rule.active || false} onChange={() => toggleActive(rule.id)} />}
							label={t(`${SAVED_RULES_KEY_PREFIX}.active`)}
						/>
						<EditButton
							onClick={() => {
								editRule(rule);
							}}
						/>
					</CardActions>
				</SpacedGridItem>
			))}
		</SpacedGridContainer>
	);
};

export default SavedRules;
